import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// AlarmTicketDetailPage.jsx - Rediseñado: subtickets como módulo de ancho completo con detalle lazy-load
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    Tooltip,
    IconButton,
    Stack,
    useTheme,
    useMediaQuery
} from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import {
    ArrowBack as BackIcon,
    CheckCircle as ResolveIcon,
    Visibility as ViewIcon,
    Download as ExportIcon,
    ExpandMore as ExpandMoreIcon,
    CallSplit as SubticketIcon
} from '@mui/icons-material';

import { useNotification } from '../components/common/GlobalNotifications';
import { useAuth } from '../hooks/useAuth';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { api } from '../services/api';
import TicketResolutionAttemptsCard from '../components/tickets/TicketResolutionAttemptsCard';
import TicketRelatedTasksCard from '../components/tickets/TicketRelatedTasksCard';
import { formatDuration as formatDurationValue } from '../utils/duration';
import VehicleSchematicSnapshot from '../components/schematics/VehicleSchematicSnapshot';
import { isDualYoyoTicket, buildDualContext } from '../utils/dualYoyo';
import TicketActionBar from '../components/tickets/TicketActionBar';
import TicketTriggerCalculation from '../components/tickets/TicketTriggerCalculation';

const TICKET_STATUS_COLORS = {
    open: 'error',
    acknowledged: 'warning',
    resolved: 'success'
};

const TICKET_STATUS_LABELS = {
    open: i18n.t('alarmTickets.status.open'),
    acknowledged: i18n.t('alarmTickets.status.acknowledged'),
    resolved: i18n.t('alarmTickets.status.resolved')
};

const BREACH_TYPE_LABELS = {
    MIN_BREACH: i18n.t('hardcoded.components_alarmprofiles_profileform.minimo'),
    MAX_BREACH: i18n.t('hardcoded.pages_billingplanspage.maximo')
};

const PRIORITY_COLORS = {
    HIGH: 'error',
    MEDIUM: 'warning',
    LOW: 'info'
};

const DELIVERY_STATUS_LABELS = {
    sent: i18n.t('hardcoded.components_admin_ordermanagement.enviado'),
    pending: i18n.t('hardcoded.components_admin_ordermanagement.pendiente'),
    failed: i18n.t('hardcoded.pages_alarmticketdetailpage.fallido'),
    suppressed: i18n.t('hardcoded.pages_alarmticketdetailpage.suprimido'),
    disabled: i18n.t('hardcoded.pages_alarmticketdetailpage.deshabilitado'),
    skipped: i18n.t('hardcoded.pages_alarmticketdetailpage.omitido')
};

const SEVERITY_COLORS = {
    alarm: 'warning',
    critical: 'error',
    extreme: 'error'
};

const SEVERITY_LABELS = {
    alarm: i18n.t('hardcoded.components_alarmprofiles_profileform.alerta_atencion'),
    critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.alarma_critica'),
    extreme: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.alarma_extrema')
};

const ALARM_REASON_LABELS = {
    pressure_threshold: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_fuera_de_regla'),
    temperature_threshold: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.temperatura_fuera_de_regla'),
    dual_yoyo: i18n.t('hardcoded.components_alarmprofiles_profileform.dual'),
    thermal_risk: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.riesgo_termico'),
    sensor_no_report: i18n.t('hardcoded.components_alarmprofiles_profileform.sensor_sin_reporte'),
    receiver_no_report: i18n.t('hardcoded.components_alarmprofiles_profileform.receptor_sin_reporte')
};

// Mapeo de códigos técnicos de regla a texto legible al usuario.
const RULE_CODE_LABELS = {
    pressure_low_alarm: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_baja'),
    pressure_high_alarm: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_alta'),
    pressure_low_critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_baja_critica'),
    pressure_high_critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_alta_critica'),
    pressure_low_extreme: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_baja_extrema'),
    pressure_high_extreme: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.presion_alta_extrema'),
    temperature_high_alarm: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.temperatura_alta'),
    temperature_high_critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.temperatura_alta_critica'),
    temperature_high_extreme: i18n.t('hardcoded.pages_alarmticketdetailpage.temperatura_alta_extrema'),
    dual_yoyo_alarm: i18n.t('hardcoded.components_alarmprofiles_profileform.dual'),
    dual_yoyo_critical: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.dual_critico'),
    dual_yoyo_extreme: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.dual_extremo'),
    thermal_risk_alarm: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.riesgo_termico'),
    sensor_no_report: i18n.t('hardcoded.components_alarmprofiles_profileform.sensor_sin_reporte'),
    receiver_no_report: i18n.t('hardcoded.components_alarmprofiles_profileform.receptor_sin_reporte'),
};
const humanizeRule = (code) => RULE_CODE_LABELS[code] || (code ? code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : i18n.t('hardcoded.pages_alarmticketdetailpage.not_available'));

const humanizeValue = (value) => {
    if (!value) return i18n.t('hardcoded.pages_alarmticketdetailpage.not_available');
    return `${value}`
        // "Yoyo" es un alias interno legacy: nunca debe verse. Se muestra como "Dual".
        .replace(/dual[_ ]?yoyo/gi, 'dual')
        .replace(/yoyo/gi, 'dual')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

// Display de "métrica" visible al usuario. Si el ticket es Dual o no-reporte,
// preferimos mostrar la razón humanizada en vez del metric_type técnico (PSI / NO_REPORT).
const displayMetricLabel = (ticket) => {
    const reason = ticket?.alarm_reason;
    if (reason === 'dual_yoyo') return 'Dual';
    if (reason === 'sensor_no_report') return 'Sin reporte (sensor)';
    if (reason === 'receiver_no_report') return 'Sin reporte (receptor)';
    const m = `${ticket?.metric_type || ''}`.toUpperCase();
    if (m === 'NO_REPORT') return 'Sin reporte';
    return `${ticket?.metric_type || ''}`.replace(/psi/gi, 'PSI');
};

const normalizePSI = (value) => {
    if (!value) return '';
    // Hide internal seconds unit
    const v = `${value}`.toLowerCase();
    if (v === 'elapsed_seconds' || v === 'h' || v === 'horas') return '';
    return `${value}`.replace(/psi/gi, 'PSI');
};

const isNoReport = (t) => `${t?.metric_type || ''}`.toUpperCase() === 'NO_REPORT';

const formatHumanElapsed = (value, unit) => {
    if (value == null) return '—';
    const u = `${unit || ''}`.toLowerCase();
    const totalSeconds = (u === 'elapsed_seconds' || u === 'seconds' || u === 's')
        ? Number(value)
        : Number(value) * 3600;
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '—';
    const days = Math.floor(totalSeconds / 86400);
    const rem = totalSeconds % 86400;
    const hours = Math.floor(rem / 3600);
    const minutes = Math.floor((rem % 3600) / 60);
    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours || days) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(' ');
};

const formatTicketValue = (t) => {
    if (!t) return '—';
    if (isNoReport(t)) return formatHumanElapsed(t.current_value, t.unit);
    const v = Number(t.current_value);
    if (!Number.isFinite(v)) return 'N/A';
    return `${v.toFixed(2)} ${normalizePSI(t.unit)}`.trim();
};

// ─────────────────────────────────────────────────────────────────────────────
// SubticketPanel: widget expandible de ancho completo para cada subticket.
// Carga el detalle completo (incluyendo chart) de forma lazy al expandir.
// ─────────────────────────────────────────────────────────────────────────────
const SubticketPanel = ({ subticket, parentUnit }) => {
  const { t } = useTranslation();
    const theme = useTheme();
    const { formatUserDateTimeSync } = useUserTimezone();
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [loadError, setLoadError] = useState(false);

    const fmt = (timestamp) => {
        if (!timestamp) return t('hardcoded.components_alarms_alarmlist.sin_datos');
        try {
            return formatUserDateTimeSync(timestamp, {
                year: 'numeric', month: 'short', day: '2-digit',
                hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
            });
        } catch {
            return new Date(timestamp).toLocaleString();
        }
    };

    const handleExpand = async (_, isExpanded) => {
        if (isExpanded && !detail && !loadingDetail) {
            setLoadingDetail(true);
            setLoadError(false);
            try {
                const response = await api.alarmTickets.getById(subticket.id);
                if (response.data) setDetail(response.data);
            } catch (err) {
                console.error('Error loading subticket detail:', err);
                setLoadError(true);
            } finally {
                setLoadingDetail(false);
            }
        }
    };

    const unit = detail?.unit || parentUnit || '';

    const subChartData = useMemo(() => {
        if (!detail) return [];
        const src = detail.chart_history?.length > 0 ? detail.chart_history : (detail.value_history || []);
        return src
            .filter(e => e?.timestamp && e?.value !== null && e?.value !== undefined)
            .map(e => ({ ms: new Date(e.timestamp).getTime(), value: Number(e.value) }))
            .filter(e => !isNaN(e.ms))
            .sort((a, b) => a.ms - b.ms);
    }, [detail]);

    const subChartOptions = useMemo(() => {
        if (!detail || subChartData.length < 2) return null;
        const startMs = detail.alarm_start_at ? new Date(detail.alarm_start_at).getTime() : null;
        const endMs = detail.alarm_end_at ? new Date(detail.alarm_end_at).getTime() : null;
        return {
            chart: { type: 'line', zoomType: 'x', backgroundColor: 'transparent', height: 220 },
            title: { text: null },
            credits: { enabled: false },
            legend: { enabled: false },
            xAxis: {
                type: 'datetime',
                plotBands: startMs && endMs ? [{
                    from: startMs, to: endMs,
                    color: `${theme.palette.warning.main}33`,
                    label: { text: t('hardcoded.pages_alarmticketdetailpage.periodo_de_alarma'), style: { color: theme.palette.warning.dark, fontSize: '10px' } }
                }] : []
            },
            yAxis: { title: { text: `${normalizePSI(detail.metric_type || '')} (${normalizePSI(unit)})` } },
            tooltip: {
                shared: true, xDateFormat: '%d/%m/%Y %H:%M:%S',
                pointFormat: `<span style="color:{series.color}">●</span> <b>{point.y:.2f} ${unit}</b><br/>`
            },
            plotOptions: { series: { marker: { enabled: true, radius: 2 } } },
            series: [{
                name: normalizePSI(detail.metric_type || t('hardcoded.components_alarms_alarmlist.metrica')),
                data: subChartData.map(e => [e.ms, e.value]),
                color: theme.palette.secondary.main,
                lineWidth: 2
            }]
        };
    }, [detail, subChartData, theme, unit]);

    return (
        <Accordion
            disableGutters
            onChange={handleExpand}
            sx={{
                mb: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { borderColor: 'primary.light' }
            }}
        >
            {/* ── Cabecera contraída ── */}
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 1, minHeight: 56 }}>
                <Grid container alignItems="center" spacing={1} sx={{ pr: 1 }}>
                    <Grid item xs={12} sm={3}>
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                            {subticket.ticket_number || `#${subticket.id?.slice(-8)}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{fmt(subticket.created_at)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <Chip
                            label={TICKET_STATUS_LABELS[subticket.status] || subticket.status}
                            color={TICKET_STATUS_COLORS[subticket.status] || 'default'}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <Typography variant="body2" fontWeight={600}>
                            {subticket.current_value !== null && subticket.current_value !== undefined
                                ? `${Number(subticket.current_value).toFixed(1)} ${normalizePSI(parentUnit)}`
                                : i18n.t('hardcoded.pages_alarmticketdetailpage.not_available')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.pages_alarmticketdetailpage.valor_registrado')}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Typography variant="caption" color="text.secondary">
                            {subticket.resolved_at ? t('hardcoded.pages_alarmticketdetailpage.resuelto_fmtsubticketres', { fmtsubticketres: fmt(subticket.resolved_at) }) : t('hardcoded.pages_alarmticketdetailpage.aun_activo')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        {subticket.within_tolerance
                            ? <Chip label={t('hardcoded.pages_alarmticketdetailpage.en_tolerancia')} size="small" color="success" variant="outlined" />
                            : <Chip label={t('hardcoded.pages_alarmticketdetailpage.fuera_tolerancia')} size="small" color="warning" variant="outlined" />
                        }
                    </Grid>
                </Grid>
            </AccordionSummary>

            {/* ── Detalle expandido ── */}
            <AccordionDetails sx={{ p: 0 }}>
                <Divider />
                {loadingDetail ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                            {i18n.t('hardcoded.pages_alarmticketdetailpage.cargando_detalle')}</Typography>
                    </Box>
                ) : loadError ? (
                    <Box sx={{ p: 2 }}>
                        <Alert severity="warning">{t('hardcoded.pages_alarmticketdetailpage.no_se_pudo_cargar_el')}</Alert>
                    </Box>
                ) : detail ? (
                    <Box sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                            {/* Info General */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>{t('hardcoded.components_schematics_vehicleticketsdrawer.informacion_general')}</Typography>
                                <List dense sx={{ py: 0 }}>
                                    <ListItem sx={{ px: 0 }}><ListItemText primary={t('vehicles.table.vehicle')} secondary={detail.vehicle_name} /></ListItem>
                                    <ListItem sx={{ px: 0 }}><ListItemText primary={t("hardcoded.pages_alarmticketdetailpage.posicion")} secondary={t('hardcoded.pages_alarmticketdetailpage.posicion_position_eje_axle', { position: detail.position, axle: detail.axle })} /></ListItem>
                                    <ListItem sx={{ px: 0 }}><ListItemText primary={t("hardcoded.pages_alarmticketdetailpage.sensor_label")} secondary={detail.sensor_id || i18n.t('hardcoded.pages_alarmticketdetailpage.not_available')} /></ListItem>
                                    <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.components_schematics_vehicleticketsdrawer.perfil_de_alerta')} secondary={detail.alarm_profile_name || t('hardcoded.components_schematics_vehicleticketsdrawer.sin_perfil')} /></ListItem>
                                    <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.components_alarms_alarmlist.metrica')} secondary={displayMetricLabel(detail)} /></ListItem>
                                </List>
                            </Grid>
                            {/* Valores y Umbrales */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>{i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.valores_y_umbrales')}</Typography>
                                <List dense sx={{ py: 0 }}>
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText primary={t("hardcoded.pages_alarmticketdetailpage.valor_original")} secondary={`${detail.original_value?.toFixed(2) ?? i18n.t('hardcoded.pages_alarmticketdetailpage.not_available')} ${normalizePSI(unit)}`} />
                                    </ListItem>
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText primary={t("hardcoded.pages_alarmticketdetailpage.valor_actual")} secondary={`${detail.current_value?.toFixed(2) ?? i18n.t('hardcoded.pages_alarmticketdetailpage.not_available')} ${normalizePSI(unit)}`} />
                                    </ListItem>
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText
                                            primary={detail.breach_type === 'MIN_BREACH' ? t('hardcoded.components_alarms_alarmform.umbral_minimo') : t('hardcoded.components_alarms_alarmform.umbral_maximo')}
                                            secondary={detail.breach_type === 'MIN_BREACH' ? `${detail.threshold_min} ${normalizePSI(unit)}` : `${detail.threshold_max} ${normalizePSI(unit)}`}
                                        />
                                    </ListItem>
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText primary={t('hardcoded.components_schematics_vehicleticketsdrawer.tipo_de_violacion')} secondary={BREACH_TYPE_LABELS[detail.breach_type] || detail.breach_type} />
                                    </ListItem>
                                </List>
                            </Grid>
                            {/* Gráfica */}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>{t('hardcoded.pages_alarmticketdetailpage.historial_de_valores')}</Typography>
                                {subChartOptions ? (
                                    <>
                                        <HighchartsReact highcharts={Highcharts} options={subChartOptions} />
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {t('hardcoded.pages_alarmticketdetailpage.rango_resaltado_periodo_activo_de')}</Typography>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_alarmticketdetailpage.no_hay_suficientes_datos_para')}</Typography>
                                )}
                            </Grid>
                            {/* Estados */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" fontWeight={700} gutterBottom>{i18n.t('hardcoded.pages_alarmticketdetailpage.estados_e_historial')}</Typography>
                                <List dense sx={{ py: 0 }}>
                                    <ListItem sx={{ px: 0 }}><ListItemText primary={t("hardcoded.pages_alarmticketdetailpage.creado_label")} secondary={fmt(detail.created_at)} /></ListItem>
                                    {detail.acknowledged_at && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemText primary={t('hardcoded.pages_alarmticketdetailpage.marcado_como_visto')} secondary={t('hardcoded.pages_alarmticketdetailpage.por_detailacknowled_fmtdetailacknow', { detailacknowled: detail.acknowledged_by || 'Sistema', fmtdetailacknow: fmt(detail.acknowledged_at) })} />
                                        </ListItem>
                                    )}
                                    {detail.resolved_at && (
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemText primary={t("hardcoded.pages_alarmticketdetailpage.resuelto_label")} secondary={t('hardcoded.pages_alarmticketdetailpage.por_detailresolved__fmtdetailresolv', { detailresolved_: detail.resolved_by || 'Sistema', fmtdetailresolv: fmt(detail.resolved_at) })} />
                                        </ListItem>
                                    )}
                                </List>
                            </Grid>
                            {/* Notas */}
                            {detail.resolution_notes && (
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>{t('hardcoded.pages_alarmticketdetailpage.notas_de_resolucion')}</Typography>
                                    <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                                        <Typography variant="body2">{detail.resolution_notes}</Typography>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                ) : null}
            </AccordionDetails>
        </Accordion>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// AlarmTicketDetailPage
// ─────────────────────────────────────────────────────────────────────────────
const AlarmTicketDetailPage = () => {
  const { t } = useTranslation();
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { showSuccess, showError, showInfo } = useNotification();
    const { currentCompany, switchCompany } = useAuth();
    const { formatUserDateTimeSync } = useUserTimezone();

    // State
    const [ticket, setTicket] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [switchingCompany, setSwitchingCompany] = useState(false);

    // Fetch ticket details — con auto-switch de empresa si el ticket pertenece a otra
    // empresa del usuario.
    useEffect(() => {
        let cancelled = false;

        const tryFetch = async () => {
            const [ticketResp, deliveriesResp] = await Promise.allSettled([
                api.alarmTickets.getById(ticketId),
                api.alarmTickets.getDeliveries(ticketId),
            ]);
            return { ticketResp, deliveriesResp };
        };

        const fetchTicket = async () => {
            try {
                setLoading(true);
                setError(null);
                let { ticketResp, deliveriesResp } = await tryFetch();

                // Si no se pudo cargar (404 típico por estar en otra empresa),
                // consultamos a qué empresa del usuario pertenece y hacemos switch automático.
                const failed = ticketResp.status !== 'fulfilled' || !ticketResp.value?.data;
                if (failed) {
                    try {
                        const lookup = await api.alarmTickets.lookupCompany(ticketId);
                        const targetCompanyId = lookup.data?.company_id;
                        const targetCompanyName = lookup.data?.company_name;
                        const currentCompanyId = currentCompany?.company_id || currentCompany?.id;
                        if (targetCompanyId && targetCompanyId !== currentCompanyId && typeof switchCompany === 'function') {
                            setSwitchingCompany(true);
                            showInfo?.(t('hardcoded.pages_alarmticketdetailpage.cambiando_a_la_empresa_targetcompanyna', { targetCompanyNa: targetCompanyName || '' }));
                            const swRes = await switchCompany(targetCompanyId);
                            setSwitchingCompany(false);
                            if (swRes?.success) {
                                // Reintentar después del switch
                                ({ ticketResp, deliveriesResp } = await tryFetch());
                            }
                        }
                    } catch (lkErr) {
                        // El usuario no pertenece a ninguna empresa que tenga este ticket → 404/403 esperado
                    }
                }

                if (cancelled) return;

                if (ticketResp.status === 'fulfilled' && ticketResp.value?.data) {
                    setTicket(ticketResp.value.data);
                } else {
                    setError(t('hardcoded.pages_alarmticketdetailpage.no_se_pudo_cargar_el'));
                }

                if (deliveriesResp.status === 'fulfilled') {
                    setDeliveries(deliveriesResp.value?.data || []);
                } else {
                    setDeliveries([]);
                }
            } catch (err) {
                console.error(t('hardcoded.pages_alarmticketdetailpage.error_fetching_ticket'), err);
                setError(t('hardcoded.pages_alarmticketdetailpage.error_cargando_detalles_del_ticket'));
                showError(t('hardcoded.pages_alarmticketdetailpage.error_cargando_ticket'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchTicket();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId]);

    const handleAcknowledge = async () => {
        try {
            await api.alarmTickets.acknowledge([ticketId]);
            showSuccess(t('hardcoded.components_schematics_vehicleticketsdrawer.ticket_marcado_como_visto'));
            setTicket({ ...ticket, status: 'acknowledged' });
        } catch (error) {
            console.error(t('hardcoded.pages_alarmticketdetailpage.error_acknowledging_ticket'), error);
            showError(t('hardcoded.pages_alarmticketdetailpage.error_marcando_como_visto'));
        }
    };

    const handleResolve = async () => {
        if (!resolutionNotes.trim()) {
            showError(t('hardcoded.pages_alarmticketdetailpage.debe_proporcionar_notas_de_resolucion'));
            return;
        }

        try {
            await api.alarmTickets.resolve(ticketId, resolutionNotes);
            showSuccess(t('hardcoded.hooks_usetickets.ticket_resuelto_exitosamente'));
            setResolveDialogOpen(false);
            setResolutionNotes('');
            setTicket({ ...ticket, status: 'resolved', resolution_notes: resolutionNotes });
        } catch (error) {
            console.error(t('hardcoded.pages_alarmticketdetailpage.error_resolving_ticket'), error);
            showError(t('hardcoded.pages_alarmticketdetailpage.error_resolviendo_ticket'));
        }
    };

    // Duracion con el helper central: a partir de 24 h se habla en dias.
    const formatDuration = (minutes) => formatDurationValue(minutes, 'minutes');

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return t('hardcoded.components_alarms_alarmlist.sin_datos');
        try {
            return formatUserDateTimeSync(timestamp, {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short'
            });
        } catch (error) {
            return new Date(timestamp).toLocaleString();
        }
    };

    const createChartData = (chartHistory, valueHistory) => {
        const sourceData = (chartHistory && chartHistory.length > 0) ? chartHistory : (valueHistory || []);
        if (!sourceData || sourceData.length === 0) return [];

        return sourceData
            .filter(entry => entry?.timestamp && entry?.value !== null && entry?.value !== undefined)
            .map((entry) => ({
                timestampMs: new Date(entry.timestamp).getTime(),
                value: Number(entry.value),
                phase: entry.phase || 'during',
                timestamp: entry.timestamp
            }))
            .filter((entry) => !Number.isNaN(entry.timestampMs))
            .sort((a, b) => a.timestampMs - b.timestampMs);
    };

    const chartData = createChartData(ticket?.chart_history, ticket?.value_history);
    const alarmStartMs = ticket?.alarm_start_at ? new Date(ticket.alarm_start_at).getTime() : null;
    const alarmEndMs = ticket?.alarm_end_at ? new Date(ticket.alarm_end_at).getTime() : null;

    const warningMain = theme.palette.warning.main;
    const warningDark = theme.palette.warning.dark;
    const primaryMain = theme.palette.primary.main;

    // Series del grupo Dual: backend devuelve `group_chart_series` con los puntos de
    // todas las posiciones del grupo en el mismo rango temporal. Ahora se renderiza UNA
    // sola gráfica con N líneas + tooltip compartido para comparar visualmente.
    const dualYoyoGroupSeries = ticket?.group_chart_series || [];
    const isDualYoyo = ticket?.alarm_reason === 'dual_yoyo' && dualYoyoGroupSeries.length > 0;

    // Umbrales del perfil (si vienen) — se dibujan como plotLines horizontales en la gráfica.
    const upperThreshold = Number.isFinite(Number(ticket?.threshold_high)) ? Number(ticket.threshold_high) : null;
    const lowerThreshold = Number.isFinite(Number(ticket?.threshold_low)) ? Number(ticket.threshold_low) : null;

    // Paleta determinística por posición — la posición del ticket es siempre PRIMARY.
    const dualPalette = ['#1976d2', '#00897b', '#7b1fa2', '#ef6c00', '#5d4037'];
    const colorForPosition = (pos, idxFallback = 0) => {
        if (Number(pos) === Number(ticket?.position)) return primaryMain;
        return dualPalette[idxFallback % dualPalette.length];
    };

    // Chart unificado. Para tickets normales = 1 serie. Para Dual = serie del ticket
    // + serie por cada otra posición del grupo, todas en el mismo eje X/Y para comparar.
    const chartOptions = useMemo(() => {
        if (!chartData || chartData.length === 0) return null;
        const unit = normalizePSI(ticket?.unit || '');
        const metricName = normalizePSI(ticket?.metric_type || t('hardcoded.components_alarms_alarmlist.metrica'));

        // Serie principal (posición del ticket).
        const mainPoints = chartData.map((entry) => [entry.timestampMs, Number(entry.value)]);
        const series = [{
            name: t('hardcoded.pages_alarmticketdetailpage.pos_ticketposition_alarma', { ticketposition: ticket?.position ?? '—' }),
            data: mainPoints,
            color: primaryMain,
            lineWidth: 2.6,
            zIndex: 5,
            marker: { enabled: true, radius: 2.5, symbol: 'circle' },
            __position: ticket?.position,
            __sensorId: ticket?.sensor_id,
            __isAlarm: true,
        }];

        // Series del grupo (solo posiciones distintas a la del ticket).
        if (isDualYoyo) {
            const extras = dualYoyoGroupSeries
                .filter((s) => Number(s.position) !== Number(ticket?.position))
                .map((s, idx) => {
                    const pts = (s.points || [])
                        .map((p) => [new Date(p.timestamp).getTime(), Number(p.value)])
                        .filter(([t, v]) => Number.isFinite(t) && Number.isFinite(v));
                    return {
                        name: t('hardcoded.pages_alarmticketdetailpage.pos_positionsis_alarm_posit', { position: s.position, sis_alarm_posit: s.is_alarm_position ? ' (alarma)' : ' (dual)' }),
                        data: pts,
                        color: colorForPosition(s.position, idx),
                        lineWidth: s.is_alarm_position ? 2.6 : 2,
                        dashStyle: s.is_alarm_position ? 'Solid' : t('hardcoded.pages_alarmticketdetailpage.shortdash'),
                        zIndex: s.is_alarm_position ? 5 : 4,
                        marker: { enabled: true, radius: 2.2, symbol: 'diamond' },
                        __position: s.position,
                        __sensorId: s.sensor_id,
                        __isAlarm: !!s.is_alarm_position,
                    };
                });
            series.push(...extras);
        }

        // PlotLines (umbrales del perfil) — un guía horizontal por threshold disponible.
        const plotLines = [];
        if (upperThreshold !== null) {
            plotLines.push({
                value: upperThreshold,
                color: warningDark,
                dashStyle: t('hardcoded.components_schematics_vehicletelemetrymodal.dash'),
                width: 1,
                zIndex: 3,
                label: { text: t('hardcoded.pages_alarmticketdetailpage.umbral_alto_upperthreshold_unit', { upperThreshold: upperThreshold, unit: unit }), align: 'right', style: { color: warningDark, fontSize: '10px' } },
            });
        }
        if (lowerThreshold !== null) {
            plotLines.push({
                value: lowerThreshold,
                color: warningDark,
                dashStyle: t('hardcoded.components_schematics_vehicletelemetrymodal.dash'),
                width: 1,
                zIndex: 3,
                label: { text: t('hardcoded.pages_alarmticketdetailpage.umbral_bajo_lowerthreshold_unit', { lowerThreshold: lowerThreshold, unit: unit }), align: 'right', style: { color: warningDark, fontSize: '10px' } },
            });
        }

        return {
            chart: { type: 'line', zoomType: 'x', backgroundColor: 'transparent', height: isDualYoyo ? 360 : 300 },
            title: { text: null },
            credits: { enabled: false },
            legend: {
                enabled: true,
                align: 'left',
                verticalAlign: 'top',
                itemStyle: { fontSize: '11px' },
            },
            xAxis: {
                type: 'datetime',
                plotBands: alarmStartMs && alarmEndMs ? [{
                    from: alarmStartMs,
                    to: alarmEndMs,
                    color: `${warningMain}26`,
                    label: { text: t('hardcoded.pages_alarmticketdetailpage.periodo_de_alarma'), style: { color: warningDark, fontSize: '10px' } },
                }] : [],
            },
            yAxis: {
                title: { text: `${metricName} (${unit})` },
                plotLines,
            },
            tooltip: {
                shared: true,
                useHTML: true,
                xDateFormat: '%d/%m/%Y %H:%M:%S',
                headerFormat: '<span style="font-size: 11px"><b>{point.key}</b></span><br/>',
                // Para Dual, mostramos diferencia entre la posición de alarma y la primera de referencia.
                formatter: function () {
                    const fmt = Highcharts.dateFormat('%d/%m/%Y %H:%M:%S', this.x);
                    let html = `<div style="font-size:11px"><b>${fmt}</b></div>`;
                    const byAlarm = [];
                    const byRef = [];
                    (this.points || []).forEach((p) => {
                        const opts = p.series.userOptions || {};
                        const line = `<div><span style="color:${p.color}">●</span> <b>Pos ${opts.__position ?? '—'}</b>${opts.__sensorId ? t('hardcoded.pages_alarmticketdetailpage.span_stylecolor888__sensoridspan', { __sensorId: opts.__sensorId }) : ''}: <b>${Number(p.y).toFixed(2)} ${unit}</b></div>`;
                            if (opts.__isAlarm) byAlarm.push({ html: line, value: p.y });
                            else byRef.push({ html: line, value: p.y });
                    });
                    html += byAlarm.map((x) => x.html).join('');
                    html += byRef.map((x) => x.html).join('');
                    if (byAlarm.length && byRef.length) {
                        const diff = byAlarm[0].value - byRef[0].value;
                        const sign = diff >= 0 ? '+' : '';
                        const refVal = byRef[0].value || 0;
                        const pct = refVal !== 0 ? ((diff / refVal) * 100).toFixed(1) : '—';
                        html += `<div style="margin-top:4px;padding-top:4px;border-top:1px dashed #ccc"><b>Δ alarma vs ref:</b> ${sign}${diff.toFixed(2)} ${unit} (${sign}${pct}%)</div>`;
                    }
                    return html;
                },
            },
            plotOptions: {
                series: {
                    marker: { enabled: true, radius: 2 },
                    states: { hover: { lineWidthPlus: 0.5 } },
                    connectNulls: false,
                },
            },
            series,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData, alarmStartMs, alarmEndMs, ticket?.metric_type, ticket?.unit, ticket?.position, ticket?.sensor_id, isDualYoyo, dualYoyoGroupSeries, primaryMain, warningDark, warningMain, upperThreshold, lowerThreshold]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !ticket) {
        return (
            <Box sx={{ p: 3 }}>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate('/alarm-tickets')}
                    sx={{ mb: 3 }}
                >
                    {i18n.t('hardcoded.pages_alarmticketdetailpage.volver_a_tickets')}</Button>
                <Alert severity="error">{error || t('hardcoded.pages_alarmticketdetailpage.ticket_no_encontrado')}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            {/* ── Header ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Button
                        startIcon={<BackIcon />}
                        onClick={() => navigate('/alarm-tickets')}
                        variant="outlined"
                        size="small"
                    >
                        {i18n.t('hardcoded.components_operators_operatorquickcreateform.volver')}</Button>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h5" fontWeight={700} noWrap>
                                {i18n.t('hardcoded.components_ai_aientitycards.ticket')}{ticket.ticket_number || ticket.id?.slice(-8)}
                            </Typography>
                            {ticket.severity && (
                                <Chip
                                    label={SEVERITY_LABELS[ticket.severity] || humanizeValue(ticket.severity)}
                                    size="small"
                                    color={SEVERITY_COLORS[ticket.severity] || 'default'}
                                />
                            )}
                            {ticket.alarm_reason && (
                                <Chip
                                    label={ALARM_REASON_LABELS[ticket.alarm_reason] || humanizeValue(ticket.alarm_reason)}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {ticket.subtickets?.length > 0 && (
                                <Chip
                                    icon={<SubticketIcon sx={{ fontSize: '0.9rem !important' }} />}
                                    label={i18n.t('hardcoded.pages_alarmticketdetailpage.subtickets', { count: ticket.subtickets.length })}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            {i18n.t('hardcoded.pages_alarmticketdetailpage.creado')}{formatTimestamp(ticket.created_at)}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {ticket.status === 'open' && (
                        <Tooltip title={t('hardcoded.components_schematics_alarmmanager.marcar_como_visto')}>
                            <Button variant="outlined" startIcon={<ViewIcon />} onClick={handleAcknowledge} size="small">
                                {i18n.t('hardcoded.pages_alarmticketdetailpage.marcar_visto')}</Button>
                        </Tooltip>
                    )}
                    {ticket.status !== 'resolved' && (
                        <Tooltip title={t('hardcoded.pages_alarmticketdetailpage.resolver_manualmente')}>
                            <Button
                                variant="contained"
                                startIcon={<ResolveIcon />}
                                onClick={() => setResolveDialogOpen(true)}
                                size="small"
                            >
                                {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.resolver')}</Button>
                        </Tooltip>
                    )}
                    <Tooltip title={t('audit.export.title')}>
                        <IconButton size="small"><ExportIcon /></IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* ── Resumen rápido (4 métricas) ── */}
            <Card sx={{ mb: 2 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{i18n.t('gps.table.status')}</Typography>
                            <Chip label={TICKET_STATUS_LABELS[ticket.status]} color={TICKET_STATUS_COLORS[ticket.status]} size="small" />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{t('hardcoded.pages_alarmticketdetailpage.duracion')}</Typography>
                            <Typography variant="body2" fontWeight={700}>{formatDuration(ticket.duration_minutes)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{t('hardcoded.components_tickets_ticketactioncenter.prioridad')}</Typography>
                            <Chip
                                label={ticket.priority}
                                color={PRIORITY_COLORS[ticket.priority] || 'default'}
                                size="small"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.regla')}</Typography>
                            <Typography variant="body2" fontWeight={700}>{humanizeRule(ticket.rule_code) !== 'N/A' ? humanizeRule(ticket.rule_code) : normalizePSI(ticket.metric_type)}</Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* ── Grid principal ── */}
            <Grid container spacing={1.5}>
                {/* Centro de acción: barra delgada (una línea) + panel lateral.
                    No roba espacio a la información del ticket. */}
                <Grid item xs={12}>
                    <TicketActionBar ticket={ticket} />
                </Grid>

                {/* Cálculo de disparo: valores del snapshot vs valores actuales.
                    Colapsado, para no desplazar la información del ticket. */}
                <Grid item xs={12}>
                    <TicketTriggerCalculation ticketId={ticket.id} />
                </Grid>

                {/* (El esquemático "Ubicación de la Alarma" se mueve a la columna
                    lateral del Historial de Valores — ver más abajo.) */}

                {/* Información General — grid interno de 2/3 columnas para reducir scroll. */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            {t('hardcoded.components_schematics_vehicleticketsdrawer.informacion_general')}</Typography>
                        <Grid container spacing={2}>
                            {[
                                { label: t('vehicles.table.vehicle'), value: ticket.vehicle_name },
                                { label: t('alarms.list.position'), value: t('hardcoded.pages_alarmticketdetailpage.posicion_position_eje_axle', { position: ticket.position, axle: ticket.axle }) },
                                { label: t('hardcoded.components_schematics_vehicleticketsdrawer.sensor'), value: ticket.sensor_id || 'N/A' },
                                { label: t('hardcoded.components_schematics_vehicleticketsdrawer.perfil_de_alerta'), value: ticket.alarm_profile_name || t('hardcoded.components_schematics_vehicleticketsdrawer.sin_perfil') },
                                { label: t('hardcoded.components_schematics_vehicleticketsdrawer.motivo'), value: ALARM_REASON_LABELS[ticket.alarm_reason] || humanizeValue(ticket.alarm_reason) },
                                { label: t('hardcoded.components_schematics_vehicleticketsdrawer.aps_severidad'), value: `${ticket.aps_level || 'N/A'} · ${SEVERITY_LABELS[ticket.severity] || humanizeValue(ticket.severity)}` },
                                { label: t('hardcoded.components_schematics_vehicleticketsdrawer.regla_aplicada'), value: humanizeRule(ticket.rule_code) },
                                { label: t('hardcoded.components_schematics_vehicleticketsdrawer.tipo_de_ticket'), value: ticket.is_subticket ? i18n.t('hardcoded.pages_alarmticketdetailpage.subticket_label') : t('hardcoded.components_schematics_vehicleticketsdrawer.ticket_padre') },
                                ticket.parent_ticket_id && {
                                    label: t('hardcoded.components_schematics_vehicleticketsdrawer.ticket_padre'),
                                    value: (
                                        <Button
                                            size="small"
                                            sx={{ px: 0, minWidth: 'auto', textTransform: 'none' }}
                                            onClick={() => navigate(`/alarm-tickets/${ticket.parent_ticket_id}`)}
                                        >
                                            {ticket.parent_ticket_code || t('hardcoded.pages_alarmticketdetailpage.ver_ticket_padre')}
                                        </Button>
                                    ),
                                },
                            ].filter(Boolean).map((field, idx) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.3 }}>
                                        {field.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                        {field.value}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Valores y Umbrales — grid interno de 2-4 columnas. */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.valores_y_umbrales')}</Typography>
                        <Grid container spacing={2}>
                            {(() => {
                                const fields = [];
                                const unit = normalizePSI(ticket.unit);
                                if (isNoReport(ticket)) {
                                    fields.push({ label: t('hardcoded.components_schematics_vehicleticketsdrawer.tiempo_sin_reportar'), value: formatHumanElapsed(ticket.current_value, ticket.unit) });
                                    fields.push({
                                        label: t('hardcoded.components_schematics_vehicleticketsdrawer.ultimo_reporte'),
                                        value: ticket.evaluation_metadata?.last_seen_at
                                            ? new Date(ticket.evaluation_metadata.last_seen_at).toLocaleString('es-MX')
                                            : '—',
                                    });
                                    fields.push({
                                        label: t('hardcoded.pages_alarmticketdetailpage.umbral_configurado'),
                                        value: ticket.evaluation_metadata?.timeout_seconds
                                            ? formatHumanElapsed(ticket.evaluation_metadata.timeout_seconds, 'elapsed_seconds')
                                            : '12h',
                                    });
                                    fields.push({
                                        label: t('vehicles.form.type'),
                                        value: ticket.alarm_reason === 'sensor_no_report'
                                            ? t('hardcoded.components_alarmprofiles_profileform.sensor_sin_reporte')
                                            : ticket.alarm_reason === 'receiver_no_report'
                                                ? t('hardcoded.components_alarmprofiles_profileform.receptor_sin_reporte')
                                                : t('hardcoded.components_schematics_vehicleticketsdrawer.sin_reporte'),
                                    });
                                } else if (isDualYoyoTicket(ticket)) {
                                    // Dual: mostrar ambas posiciones y la diferencia, NO una sola lectura.
                                    const dctx = buildDualContext(ticket) || {};
                                    fields.push({ label: t('vehicles.form.type'), value: t('hardcoded.components_alarmprofiles_profileform.dual') });
                                    fields.push({ label: t('alarms.list.position'), value: dctx.positionsLabel || t('hardcoded.components_alarmprofiles_profileform.dual') });
                                    fields.push({ label: t('hardcoded.components_alarms_alarmlist.metrica'), value: t('hardcoded.pages_alarmticketdetailpage.dual_psi') });
                                    fields.push({
                                        label: t('hardcoded.pages_alarmticketdetailpage.valor_actual_diferencia'),
                                        value: dctx.suspended ? t('hardcoded.pages_alarmticketdetailpage.suspendido') : (dctx.valueActualDisplay || '—'),
                                        highlight: !dctx.suspended,
                                    });
                                    fields.push({ label: t('hardcoded.pages_alarmticketdetailpage.rango_permitido'), value: dctx.rangeDisplay || '—' });
                                    if (dctx.primaryDisplay) fields.push({ label: t('hardcoded.pages_alarmticketdetailpage.posicion_a'), value: dctx.primaryDisplay });
                                    if (dctx.secondaryDisplay) fields.push({ label: t('hardcoded.pages_alarmticketdetailpage.posicion_b'), value: dctx.secondaryDisplay });
                                    if (dctx.profileName) fields.push({ label: t('navbar.profile'), value: dctx.profileName });
                                    fields.push({ label: t('hardcoded.components_settings_notificationpreferenceseditor.regla'), value: t('hardcoded.components_alarmprofiles_profileform.dual') });
                                } else {
                                    fields.push({
                                        label: t('hardcoded.components_alarms_alarmlist.valor_actual'),
                                        value: `${ticket.current_value?.toFixed(2) ?? 'N/A'} ${unit}`,
                                        highlight: true,
                                    });
                                    fields.push({
                                        label: t('hardcoded.components_schematics_vehicleticketsdrawer.valor_original'),
                                        value: `${ticket.original_value?.toFixed(2) ?? 'N/A'} ${unit}`,
                                    });
                                    fields.push({
                                        label: ticket.breach_type === 'MIN_BREACH' ? t('hardcoded.components_alarms_alarmform.umbral_minimo') : t('hardcoded.components_alarms_alarmform.umbral_maximo'),
                                        value: ticket.breach_type === 'MIN_BREACH'
                                            ? `${ticket.threshold_min ?? '—'} ${unit}`
                                            : `${ticket.threshold_max ?? '—'} ${unit}`,
                                    });
                                    fields.push({
                                        label: t('hardcoded.components_schematics_vehicleticketsdrawer.tipo_de_violacion'),
                                        value: BREACH_TYPE_LABELS[ticket.breach_type] || ticket.breach_type || '—',
                                    });
                                    if (ticket.resolution_range_min != null || ticket.resolution_range_max != null) {
                                        fields.push({
                                            label: t('hardcoded.components_schematics_vehicleticketsdrawer.rango_de_cierre'),
                                            value: `${ticket.resolution_range_min ?? '...'} a ${ticket.resolution_range_max ?? '...'} ${unit}`,
                                        });
                                    }
                                }
                                return fields.map((field, idx) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.3 }}>
                                            {field.label}
                                        </Typography>
                                        <Typography variant={field.highlight ? 'h6' : 'body2'} sx={{ wordBreak: 'break-word', fontWeight: field.highlight ? 700 : 400, color: field.highlight ? 'error.main' : 'text.primary' }}>
                                            {field.value}
                                        </Typography>
                                    </Grid>
                                ));
                            })()}
                        </Grid>
                        {isDualYoyoTicket(ticket) && (() => {
                            const dctx = buildDualContext(ticket);
                            if (dctx?.suspended) {
                                const stale = (dctx.stalePositions || []).join(' y ') || t('hardcoded.pages_alarmticketdetailpage.comparada');
                                return (
                                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                                        {t('hardcoded.pages_alarmticketdetailpage.evaluacion_dual_suspendida_la_posicion', { stale: stale })}
                                    </Alert>
                                );
                            }
                            return null;
                        })()}
                        {ticket.reason_text && (
                            <Alert severity="info" sx={{ mt: 1.5 }}>
                                {ticket.reason_text}
                            </Alert>
                        )}
                    </Paper>
                </Grid>

                {/* Historial de Valores — UNA sola gráfica con N series.
                    Para tickets Dual se superponen las series del grupo en el mismo
                    eje X/Y, con leyenda + tooltip compartido + diferencia PSI calculada. */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            {t('hardcoded.pages_alarmticketdetailpage.historial_de_valores')}{isDualYoyo && (
                                <Chip size="small" color="error" label={t('hardcoded.pages_alarmticketdetailpage.dual_length_posiciones', { length: dualYoyoGroupSeries.length })} sx={{ ml: 1, height: 20 }} />
                            )}
                        </Typography>

                        {chartOptions && chartData.length > 1 ? (
                            <Box>
                                <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {t('hardcoded.pages_alarmticketdetailpage.tip_selecciona_con_el_cursor')}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {i18n.t('hardcoded.pages_alarmticketdetailpage.rango_resaltado_periodo_activo')}{formatTimestamp(ticket.alarm_start_at)} — {ticket.alarm_end_at ? formatTimestamp(ticket.alarm_end_at) : t('hardcoded.pages_alarmticketdetailpage.aun_activa')})
                                </Typography>
                                {isDualYoyo && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {t('hardcoded.pages_alarmticketdetailpage.tooltip_muestra_ambas_lecturas_y')}</Typography>
                                )}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {t('hardcoded.pages_alarmticketdetailpage.no_hay_suficientes_datos_para')}</Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Columna lateral: Esquemático enfocado en la alarma + Estados e Historial.
                    El esquemático usa el MISMO PositionTile que el panel de control,
                    sin lógica viva (no llama tickets/websocket). */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={1.5} sx={{ height: '100%' }}>
                        <Paper sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {t('hardcoded.pages_alarmticketdetailpage.ubicacion_de_la_alarma')}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {ticket.position
                                        ? t('hardcoded.pages_alarmticketdetailpage.pos_position_eje_axle', { position: ticket.position, axle: ticket.axle })
                                        : t('hardcoded.pages_alarmticketdetailpage.posicion_no_disponible')}
                                </Typography>
                            </Box>
                            <VehicleSchematicSnapshot
                                vehicle={{
                                    id: ticket.vehicle_id,
                                    alias: ticket.vehicle_name,
                                    figure: ticket.vehicle_figure,
                                    positions_count: ticket.vehicle_positions_count,
                                    // Sin esto el esquematico no sabe que posiciones tienen
                                    // sensor y las pintaba TODAS como "NO SENSOR".
                                    vehicle_sensors: (ticket.vehicle_sensor_positions || [])
                                        .map((p) => ({ position: Number(p) })),
                                }}
                                highlightPositions={
                                    isDualYoyo
                                        ? (ticket.evaluation_metadata?.group_positions
                                            || dualYoyoGroupSeries.filter((s) => s.is_alarm_position).map((s) => s.position)
                                            || [ticket.position]).filter(Boolean)
                                        : [ticket.position].filter(Boolean)
                                }
                                groupPositions={
                                    isDualYoyo
                                        ? (ticket.evaluation_metadata?.group_positions
                                            || dualYoyoGroupSeries.map((s) => s.position)
                                            || []).filter(Boolean)
                                        : []
                                }
                                alarmType={ticket.alarm_reason || ticket.rule_code}
                                metricType={ticket.metric_type || 'PSI'}
                                dense
                            />
                        </Paper>

                        <Paper sx={{ p: 2, flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                {i18n.t('hardcoded.pages_alarmticketdetailpage.estados_e_historial')}</Typography>
                        <List dense sx={{ py: 0 }}>
                            <ListItem sx={{ px: 0 }}>
                                <ListItemText primary={t("hardcoded.pages_alarmticketdetailpage.creado_label")} secondary={formatTimestamp(ticket.created_at)} />
                            </ListItem>
                            {ticket.acknowledged_at && (
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemText
                                        primary={t('hardcoded.pages_alarmticketdetailpage.marcado_como_visto')}
                                        secondary={t('hardcoded.pages_alarmticketdetailpage.por_ticketacknowled_formattimestamp', { ticketacknowled: ticket.acknowledged_by || 'Sistema', formatTimestamp: formatTimestamp(ticket.acknowledged_at) })}
                                    />
                                </ListItem>
                            )}
                            {ticket.resolved_at && (
                                <ListItem sx={{ px: 0 }}>
                                    <ListItemText
                                        primary={t("hardcoded.pages_alarmticketdetailpage.resuelto_label")}
                                        secondary={t('hardcoded.pages_alarmticketdetailpage.por_ticketresolved__formattimestamp', { ticketresolved_: ticket.resolved_by || 'Sistema', formatTimestamp: formatTimestamp(ticket.resolved_at) })}
                                    />
                                </ListItem>
                            )}
                            {ticket.resolution_notes && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <ListItem sx={{ px: 0 }}>
                                        <ListItemText primary={t('hardcoded.pages_alarmticketdetailpage.notas_de_resolucion')} secondary={ticket.resolution_notes} />
                                    </ListItem>
                                </>
                            )}
                            <Divider sx={{ my: 1 }} />
                            <ListItem sx={{ px: 0 }}>
                                <ListItemText
                                    primary={t("hardcoded.pages_alarmticketdetailpage.notificaciones")}
                                    secondary={
                                        deliveries.length > 0
                                            ? `${deliveries.filter(d => d.success || d.delivery_status === 'sent').length}/${deliveries.length} intentos exitosos`
                                            : t('hardcoded.pages_alarmticketdetailpage.sin_entregas_registradas_para_este')
                                    }
                                />
                            </ListItem>
                            {deliveries.slice(0, 6).map((delivery) => (
                                <ListItem key={delivery.id} sx={{ px: 0 }}>
                                    <ListItemText
                                        primary={`${delivery.channel?.toUpperCase() || i18n.t('hardcoded.pages_alarmticketdetailpage.canal')} · ${DELIVERY_STATUS_LABELS[delivery.delivery_status || delivery.status] || (delivery.success ? i18n.t('hardcoded.pages_alarmticketdetailpage.enviado') : i18n.t('hardcoded.pages_alarmticketdetailpage.fallido'))}`}
                                        secondary={`${delivery.recipient_contact || delivery.recipient_name || t('hardcoded.pages_alarmticketdetailpage.destino_no_disponible')}${delivery.status_reason ? ` · ${delivery.status_reason}` : ''}${delivery.error_message ? ` · ${delivery.error_message}` : ''}${delivery.attempted_at ? ` · ${formatTimestamp(delivery.attempted_at)}` : ''}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        </Paper>

                        {/* Por que el sistema cerro (o no cerro) este ticket. */}
                        <TicketResolutionAttemptsCard ticketId={ticketId} formatTimestamp={formatTimestamp} />

                        {/* Trabajo asociado al ticket, no solo "hay tarea o no". */}
                        <TicketRelatedTasksCard ticketId={ticketId} formatTimestamp={formatTimestamp} />
                    </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            {t('hardcoded.pages_alarmticketdetailpage.evaluacion_y_canales')}</Typography>

                        {/* Canales solicitados: filtra los canales reales (portal/email/whatsapp/voice/sms)
                            y separa la política de voz (voice_policy) en su propio campo. */}
                        {ticket.channels_snapshot && (() => {
                            const CHANNEL_LABELS = { portal: i18n.t('hardcoded.pages_alarmticketdetailpage.portal_label'), email: 'Email', whatsapp: 'WhatsApp', voice: t('hardcoded.components_chat_calloverlay.llamada'), sms: 'SMS' };
                            const CHANNEL_KEYS = ['portal', 'email', 'whatsapp', 'voice', 'sms'];
                            const VOICE_POLICY_LABELS = { immediate: i18n.t('hardcoded.pages_alarmticketdetailpage.inmediata'), persistent: t('hardcoded.pages_alarmticketdetailpage.persistente'), disabled: t('hardcoded.pages_alarmticketdetailpage.desactivada') };
                            const active = CHANNEL_KEYS.filter((k) => ticket.channels_snapshot?.[k]);
                            const voicePolicy = ticket.channels_snapshot?.voice_policy;
                            return (
                                <Box sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                                        {i18n.t('hardcoded.pages_alarmticketdetailpage.canales_solicitados')}</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: voicePolicy ? 1.5 : 0 }}>
                                        {active.length ? active.map((ch) => (
                                            <Chip key={ch} label={CHANNEL_LABELS[ch] || ch} size="small" color="primary" variant="outlined" />
                                        )) : (
                                            <Typography variant="body2" color="text.secondary">{i18n.t('hardcoded.pages_alarmticketdetailpage.ninguno_activo')}</Typography>
                                        )}
                                    </Box>
                                    {voicePolicy && (
                                        <>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                                                {t('hardcoded.pages_alarmticketdetailpage.politica_de_llamada')}</Typography>
                                            <Typography variant="body2">{VOICE_POLICY_LABELS[voicePolicy] || humanizeValue(voicePolicy)}</Typography>
                                        </>
                                    )}
                                </Box>
                            );
                        })()}

                        {/* Evaluación: render humanizado de evaluation_metadata. */}
                        {ticket.evaluation_metadata && Object.keys(ticket.evaluation_metadata).length > 0 && (() => {
                            const META = ticket.evaluation_metadata;
                            const COMPARISON_LABELS = { median: i18n.t('hardcoded.pages_alarmticketdetailpage.mediana'), average: i18n.t('hardcoded.pages_alarmticketdetailpage.promedio'), ideal: t('hardcoded.pages_alarmticketdetailpage.presion_ideal') };
                            const fields = [];
                            const push = (label, value, opts = {}) => {
                                if (value === null || value === undefined || value === '') return;
                                fields.push({ label, value, ...opts });
                            };
                            push(t('hardcoded.pages_alarmticketdetailpage.tipo_de_evaluacion'), META.evaluation_type === 'dual_yoyo' ? t('hardcoded.components_alarmprofiles_profileform.dual') : (META.evaluation_type ? humanizeValue(META.evaluation_type) : null));
                            if (Array.isArray(META.group_positions) && META.group_positions.length) {
                                push(t('hardcoded.pages_alarmticketdetailpage.posiciones_del_grupo'), (
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                                        {META.group_positions.map((p) => (
                                            <Chip key={p} size="small" label={t('hardcoded.pages_alarmticketdetailpage.pos_p', { p: p })} color={Number(p) === Number(ticket.position) ? 'error' : 'default'} variant={Number(p) === Number(ticket.position) ? 'filled' : 'outlined'} />
                                        ))}
                                    </Box>
                                ));
                            }
                            if (META.reference_value != null) push(t('hardcoded.pages_alarmticketdetailpage.valor_de_referencia'), `${Number(META.reference_value).toFixed(2)} ${ticket.unit || ''}`);
                            if (META.comparison_method) push(t('hardcoded.pages_alarmticketdetailpage.metodo_de_comparacion'), COMPARISON_LABELS[META.comparison_method] || humanizeValue(META.comparison_method));
                            if (META.difference_percent != null) push(t('hardcoded.pages_alarmticketdetailpage.diferencia'), `${Number(META.difference_percent).toFixed(2)} %`, { highlight: true });
                            // signed_difference_percent: mostrar solo si difiere del valor absoluto (signo importante).
                            if (META.signed_difference_percent != null && Number(META.signed_difference_percent) !== Math.abs(Number(META.difference_percent ?? 0))) {
                                push(t('hardcoded.pages_alarmticketdetailpage.diferencia_con_signo'), `${Number(META.signed_difference_percent).toFixed(2)} %`);
                            }
                            if (META.last_seen_at) push(t('hardcoded.components_schematics_vehicleticketsdrawer.ultimo_reporte'), new Date(META.last_seen_at).toLocaleString('es-MX'));
                            if (META.timeout_seconds) push(t('hardcoded.pages_alarmticketdetailpage.umbral_de_no_reporte'), formatHumanElapsed(META.timeout_seconds, 'elapsed_seconds'));

                            // Umbrales del perfil (contexto útil, en %). Se OCULTAN las claves
                            // técnicas/internas del evaluador (side, suspended, stale_limit_seconds,
                            // primary_value, current_value, axle_number, dual_yoyo, etc.) para no saturar.
                            const pct = (v) => `${Number(v)}%`;
                            if (META.allowed_difference_percent != null) push(t('hardcoded.pages_alarmticketdetailpage.umbral_permitido'), pct(META.allowed_difference_percent));
                            if (META.critical_difference_percent != null) push(t('hardcoded.pages_alarmticketdetailpage.umbral_critico'), pct(META.critical_difference_percent));
                            if (META.extreme_difference_percent != null) push(t('hardcoded.pages_alarmticketdetailpage.umbral_extremo'), pct(META.extreme_difference_percent));
                            if (META.threshold_psi_approx != null) push(t('hardcoded.pages_alarmticketdetailpage.umbral_permitido_psi'), `≈${Number(META.threshold_psi_approx)} PSI`);

                            return (
                                <Grid container spacing={1.5}>
                                    {fields.map((field, idx) => (
                                        <Grid item xs={12} sm={6} key={idx}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.3 }}>
                                                {field.label}
                                            </Typography>
                                            <Typography
                                                variant={field.highlight ? 'h6' : 'body2'}
                                                sx={{ wordBreak: 'break-word', fontWeight: field.highlight ? 700 : 400, color: field.highlight ? 'error.main' : 'text.primary' }}
                                                component="div"
                                            >
                                                {field.value}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            );
                        })()}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                            {t('hardcoded.pages_alarmticketdetailpage.historial_del_evento')}</Typography>
                        {Array.isArray(ticket.event_log) && ticket.event_log.length > 0 ? (() => {
                            const EVENT_LABELS = {
                                created: t('hardcoded.pages_alarmticketdetailpage.ticket_creado'),
                                acknowledged: t('hardcoded.pages_alarmticketdetailpage.marcado_como_visto'),
                                resolved: t('alarmTickets.status.resolved'),
                                auto_resolved: t('hardcoded.pages_alarmticketdetailpage.resuelto_automaticamente'),
                                threshold_breach: t('hardcoded.pages_alarmticketdetailpage.umbral_violado'),
                                value_update: t('hardcoded.pages_alarmticketdetailpage.valor_actualizado'),
                                no_report_detected: t('hardcoded.pages_alarmticketdetailpage.falta_de_reporte_detectada'),
                                escalated: t('hardcoded.pages_alarmticketdetailpage.escalado'),
                            };
                            return (
                                <List dense sx={{ py: 0 }}>
                                    {ticket.event_log.slice().reverse().map((event, index) => {
                                        const type = String(event.event_type || event.type || t('hardcoded.components_settings_notificationpreferenceseditor.evento')).toLowerCase();
                                        return (
                                            <ListItem key={`${type}-${index}`} sx={{ px: 0, alignItems: 'flex-start' }}>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {EVENT_LABELS[type] || humanizeValue(type)}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {formatTimestamp(event.timestamp || event.at || event.created_at)}
                                                            </Typography>
                                                            {event.message && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                    {event.message}
                                                                </Typography>
                                                            )}
                                                            {event.details && typeof event.details === 'object' && (
                                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                                                    {Object.entries(event.details).map(([k, v]) => (
                                                                        v !== null && v !== undefined && typeof v !== 'object' && (
                                                                            <Chip key={k} size="small" variant="outlined" label={`${humanizeValue(k)}: ${v}`} sx={{ height: 18, fontSize: '0.65rem' }} />
                                                                        )
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            );
                        })() : (
                            <Typography variant="body2" color="text.secondary">{t('hardcoded.pages_alarmticketdetailpage.sin_eventos_registrados')}</Typography>
                        )}
                    </Paper>
                </Grid>

                {/* ── Sección de Subtickets — ancho completo, módulo independiente ── */}
                {ticket.subtickets?.length > 0 && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                <SubticketIcon color="info" />
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {i18n.t('hardcoded.pages_alarmticketdetailpage.subtickets_asociados')}</Typography>
                                <Chip
                                    label={t('hardcoded.pages_alarmticketdetailpage.length_eventoticketsubticket', { length: ticket.subtickets.length, ticketsubticket: ticket.subtickets.length !== 1 ? 's' : '' })}
                                    size="small"
                                    color="info"
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {t('hardcoded.pages_alarmticketdetailpage.cada_subticket_representa_un_evento')}</Typography>
                            {ticket.subtickets.map((subticket) => (
                                <SubticketPanel key={subticket.id} subticket={subticket} parentUnit={ticket.unit} />
                            ))}
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* ── Diálogo resolver ── */}
            <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle>{i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.resolver_ticket')}{ticket.ticket_number || ticket.id?.slice(-8)}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label={t('hardcoded.pages_alarmticketdetailpage.notas_de_resolucion')}
                            value={resolutionNotes}
                            onChange={(e) => setResolutionNotes(e.target.value)}
                            placeholder={t('hardcoded.pages_alarmticketdetailpage.describa_el_motivo_y_acciones')}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolveDialogOpen(false)}>{t('common.cancel')}</Button>
                    <Button onClick={handleResolve} variant="contained" disabled={!resolutionNotes.trim()}>
                        {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.resolver')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AlarmTicketDetailPage;
